import { useCallback, useEffect, useRef, useState } from 'react';
import { Note, Panel, Spinner } from '@wyocspay/ui';
import { ApiError, api, type LogoState } from './api.js';
import { LogoCropModal } from './LogoCropModal.js';

/**
 * The merchant's own logo, and the one place it is uploaded from.
 *
 * The same component serves the settings area and the onboarding prompt,
 * because "add your logo" should not be two slightly different forms that drift
 * apart. Onboarding passes `compact` and a skip handler; settings passes
 * neither.
 *
 * Everything about this is optional. A merchant who never uploads anything has
 * a payment page identical to the one they had before, so nothing here nags,
 * nothing is marked incomplete, and removing a logo is offered as plainly as
 * adding one.
 */

/**
 * What the file picker offers.
 *
 * A hint, not a control: the server decides the type from the file's own
 * leading bytes and refuses anything else, because an `accept` attribute is a
 * convenience for the person choosing a file and no kind of check. SVG is
 * absent here and refused there - it can carry script, and an SVG served from
 * the payment page's origin would run as that origin.
 */
const ACCEPT = 'image/png,image/jpeg,image/webp';

function bytes(n: number): string {
  return n < 1024 ? `${n} B` : `${Math.round(n / 1024)} KB`;
}

/**
 * The current logo, fetched with the session token and rendered from an object
 * URL.
 *
 * The bytes need an Authorization header, so a plain `src` pointing at the API
 * would simply come back 401. The object URL is revoked when it is replaced or
 * when the component goes away, so a merchant who tries three files in a row
 * does not leave three of them alive in the tab.
 */
function LogoPreview({ version, height = 44 }: { version: number; height?: number }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;

    void api
      .logoImage()
      .then((blob) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(blob);
        setUrl(revoked);
      })
      .catch(() => {
        // A preview that will not load is not worth an error message on a page
        // about a decorative image.
      });

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [version]);

  if (!url) return null;
  return (
    <img
      src={url}
      alt="Your logo"
      style={{
        display: 'block',
        height,
        maxWidth: 220,
        width: 'auto',
        objectFit: 'contain',
      }}
    />
  );
}

export function BrandLogo({
  compact = false,
  initial,
  onSkip,
  onChange,
}: {
  /** Onboarding shows a shorter version of the same thing. */
  compact?: boolean;
  /**
   * State the caller has already fetched.
   *
   * Onboarding has to know whether there is a logo before it can decide whether
   * to ask for one, so it already holds the answer; passing it in saves the
   * same read being made twice on the same screen.
   */
  initial?: LogoState;
  /** Present only where "not now" is an answer, which is onboarding. */
  onSkip?: () => void;
  onChange?: (state: LogoState) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<LogoState | null>(initial ?? null);
  const [loading, setLoading] = useState(initial === undefined);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  /** The file the merchant just picked, being cropped in the modal. */
  const [pending, setPending] = useState<File | null>(null);
  /** Bumped on every change, so the preview refetches rather than caching. */
  const [version, setVersion] = useState(0);

  const apply = useCallback(
    (next: LogoState) => {
      setState(next);
      setVersion((v) => v + 1);
      onChange?.(next);
    },
    [onChange],
  );

  useEffect(() => {
    if (initial !== undefined) return;
    let cancelled = false;
    api
      .logo()
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((err) => {
        if (!cancelled) {
          setMessage({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Fetching once on mount; a caller that supplies `initial` never fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Save the already-cropped, already-shrunk image from the modal.
   *
   * No size pre-check here anymore: the merchant may pick a file of any size,
   * because the crop modal reads it locally and sends back a small WebP/PNG that
   * fits the server's cap. This rethrows on failure so the modal can show the
   * error and stay open for a retry; on success we close it and confirm.
   */
  async function saveCropped(base64: string): Promise<void> {
    const next = await api.setLogo(base64);
    apply(next);
    setPending(null);
    setMessage({ tone: 'ok', text: 'Saved. Your customers will see it on your payment pages.' });
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setMessage(null);
    try {
      apply(await api.removeLogo());
      setMessage({ tone: 'ok', text: 'Removed. Your payment pages now show your business name on its own.' });
    } catch (err) {
      setMessage({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function skip(): Promise<void> {
    setBusy(true);
    try {
      apply(await api.skipLogo());
      onSkip?.();
    } catch (err) {
      setMessage({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <div style={{ display: 'grid', gap: 14 }}>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared immediately so choosing the same file twice still fires.
          e.target.value = '';
          // Opens the crop modal rather than uploading straight away.
          if (file) { setMessage(null); setPending(file); }
        }}
      />

      {loading ? (
        <Spinner label="Loading…" />
      ) : (
        <>
          {state?.present ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
                padding: 14,
                border: '1px solid var(--wc-border)',
                background: 'var(--wc-surface)',
              }}
            >
              <LogoPreview version={version} />
              <div style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                {state.mime_type} · {state.size === null ? '' : bytes(state.size)}
              </div>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {state?.present ? 'Replace logo' : 'Upload logo'}
            </button>
            {state?.present ? (
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                disabled={busy}
                onClick={() => void remove()}
              >
                Remove
              </button>
            ) : null}
            {onSkip && !state?.present ? (
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                disabled={busy}
                onClick={() => void skip()}
              >
                Not now
              </button>
            ) : null}
          </div>

          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.5 }}>
            PNG, JPEG or WebP - pick any size and crop it in the next step; it is optimized for you
            automatically. It is shown at about forty pixels tall beside your business name, so a wide
            mark reads better than a tall one, and a transparent PNG sits best on the dark band. SVG is
            not accepted: it is a document that can carry code, and nothing that can run belongs on a
            page where somebody is typing a card number.
          </p>
        </>
      )}

      {message ? <Note tone={message.tone === 'ok' ? 'ok' : 'danger'}>{message.text}</Note> : null}

      {pending ? (
        <LogoCropModal file={pending} onCancel={() => setPending(null)} onSave={saveCropped} />
      ) : null}
    </div>
  );

  if (compact) return body;

  return (
    <Panel title="Your logo">
      <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
        Your customers pay on <strong>pay.wyocs.org</strong>, which is our address rather than yours.
        Your business name is already shown on every screen of that flow; a logo beside it is what
        makes a customer certain, at a glance, that they are in the right place. It is optional - with
        no logo the page shows your name on its own, exactly as it does today.
      </p>
      {body}
    </Panel>
  );
}
